import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    ArrowRight,
    CheckCircle2,
    MapPin,
    MessageCircle,
    Phone,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero } from "@/components/PageHero";
import { Logo } from "@/components/Logo";
import {
    API,
    CITY_OPTIONS,
    IMG,
    INPUT_CLASS,
    LOCATIONS,
    PHONE_DISPLAY,
    SERVICE_OPTIONS,
    TEL_URL,
    WA_URL,
    buildWaQuoteUrl,
} from "@/lib/site";

const EMPTY_FORM = {
    full_name: "",
    phone: "",
    email: "",
    city: "",
    service_required: "",
    message: "",
};

const validate = (f) => {
    const errors = {};
    if (f.full_name.trim().length < 2) errors.full_name = "Please enter your full name.";
    if (!/^[0-9+\-\s()]{8,20}$/.test(f.phone.trim()))
        errors.phone = "Please enter a valid phone number.";
    if (f.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim()))
        errors.email = "Please enter a valid email address.";
    if (!f.city) errors.city = "Please select your city.";
    if (!f.service_required) errors.service_required = "Please select a service.";
    return errors;
};

const FieldError = ({ id, message }) =>
    message ? (
        <p data-testid={`${id}-error`} className="mt-1.5 text-xs font-semibold text-[#9F1239]">
            {message}
        </p>
    ) : null;

export default function Contact() {
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const update = (key) => (e) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        setErrors((er) => ({ ...er, [key]: undefined }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const errs = validate(form);
        setErrors(errs);
        if (Object.keys(errs).length > 0) return;
        setSubmitting(true);
        try {
            await axios.post(`${API}/enquiries`, {
                full_name: form.full_name.trim(),
                phone: form.phone.trim(),
                email: form.email.trim() || null,
                city: form.city,
                service_required: form.service_required,
                message: form.message.trim() || null,
            });
            setSubmitted(true);
            setForm(EMPTY_FORM);
            toast.success("Enquiry received. K1 ELEVATOR'S will contact you shortly.");
        } catch (err) {
            toast.error("Could not submit your enquiry. Please call or WhatsApp us directly.");
        } finally {
            setSubmitting(false);
        }
    };

    const sendViaWhatsApp = () => {
        if (
            form.full_name.trim().length < 2 ||
            !/^[0-9+\-\s()]{8,20}$/.test(form.phone.trim())
        ) {
            toast.error("Please enter your name and a valid phone number first.");
            return;
        }
        window.open(
            buildWaQuoteUrl({
                name: form.full_name.trim(),
                phone: form.phone.trim(),
                city: form.city,
                service: form.service_required,
                message: form.message.trim(),
            }),
            "_blank",
            "noopener",
        );
    };

    return (
        <>
            <Seo
                title="Contact K1 ELEVATOR'S | Lift Service & Breakdown Support — Patna, Gaya, Ranchi"
                description="Contact K1 ELEVATOR'S for lift installation, elevator maintenance, AMC and breakdown service in Patna, Gaya and Ranchi. Call or WhatsApp +91 89694 49945."
            />

            <PageHero
                eyebrow="Contact Us"
                title="LET'S TALK ABOUT YOUR ELEVATOR"
                subtitle="Need a new lift, regular maintenance or urgent breakdown assistance? Contact K1 ELEVATOR'S."
                image={IMG.lobby}
                testid="contact-hero-heading"
            />

            {/* Form + info */}
            <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
                <div className="grid gap-12 lg:grid-cols-3">
                    <Reveal className="lg:col-span-2">
                        <div className="rounded-3xl border border-slate-200/90 bg-white p-7 shadow-[0_12px_40px_rgb(15,23,42,0.07)] sm:p-10">
                            <h2 className="text-2xl font-extrabold tracking-tight text-[#0B1528]">
                                Request a Free Quote
                            </h2>
                            <p className="mt-2 text-sm text-slate-500">
                                Fill in the details below and our team will get back to you.
                            </p>

                            {submitted && (
                                <div
                                    data-testid="contact-success-message"
                                    className="mt-6 flex items-start gap-3 rounded-xl border border-blue-200 bg-[#F0F5FD] p-4"
                                >
                                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#1D4ED8]" />
                                    <div>
                                        <p className="text-sm font-bold text-[#0B1528]">
                                            Thank you — your enquiry has been received.
                                        </p>
                                        <p className="mt-1 text-sm text-slate-600">
                                            K1 ELEVATOR&rsquo;S will contact you shortly. For urgent
                                            breakdowns, call {PHONE_DISPLAY} directly.
                                        </p>
                                    </div>
                                </div>
                            )}

                            <form onSubmit={onSubmit} noValidate className="mt-8 grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="contact-name" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-600">
                                        Full Name *
                                    </label>
                                    <input
                                        id="contact-name"
                                        data-testid="contact-form-name"
                                        type="text"
                                        value={form.full_name}
                                        onChange={update("full_name")}
                                        placeholder="Your full name"
                                        className={INPUT_CLASS}
                                    />
                                    <FieldError id="contact-form-name" message={errors.full_name} />
                                </div>
                                <div>
                                    <label htmlFor="contact-phone" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-600">
                                        Phone Number *
                                    </label>
                                    <input
                                        id="contact-phone"
                                        data-testid="contact-form-phone"
                                        type="tel"
                                        value={form.phone}
                                        onChange={update("phone")}
                                        placeholder="+91 XXXXX XXXXX"
                                        className={INPUT_CLASS}
                                    />
                                    <FieldError id="contact-form-phone" message={errors.phone} />
                                </div>
                                <div>
                                    <label htmlFor="contact-email" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-600">
                                        Email
                                    </label>
                                    <input
                                        id="contact-email"
                                        data-testid="contact-form-email"
                                        type="email"
                                        value={form.email}
                                        onChange={update("email")}
                                        placeholder="you@example.com"
                                        className={INPUT_CLASS}
                                    />
                                    <FieldError id="contact-form-email" message={errors.email} />
                                </div>
                                <div>
                                    <label htmlFor="contact-city" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-600">
                                        City *
                                    </label>
                                    <select
                                        id="contact-city"
                                        data-testid="contact-form-city"
                                        value={form.city}
                                        onChange={update("city")}
                                        className={INPUT_CLASS}
                                    >
                                        <option value="">Select city</option>
                                        {CITY_OPTIONS.map((c) => (
                                            <option key={c} value={c}>
                                                {c}
                                            </option>
                                        ))}
                                    </select>
                                    <FieldError id="contact-form-city" message={errors.city} />
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="contact-service" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-600">
                                        Service Required *
                                    </label>
                                    <select
                                        id="contact-service"
                                        data-testid="contact-form-service"
                                        value={form.service_required}
                                        onChange={update("service_required")}
                                        className={INPUT_CLASS}
                                    >
                                        <option value="">Select a service</option>
                                        {SERVICE_OPTIONS.map((s) => (
                                            <option key={s} value={s}>
                                                {s}
                                            </option>
                                        ))}
                                    </select>
                                    <FieldError id="contact-form-service" message={errors.service_required} />
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="contact-message" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-600">
                                        Message
                                    </label>
                                    <textarea
                                        id="contact-message"
                                        data-testid="contact-form-message"
                                        rows={4}
                                        value={form.message}
                                        onChange={update("message")}
                                        placeholder="Tell us about your building, elevator or the issue you are facing..."
                                        className={`${INPUT_CLASS} resize-y`}
                                    />
                                </div>
                                <div className="flex flex-col gap-3 sm:col-span-2 sm:flex-row">
                                    <button
                                        type="submit"
                                        data-testid="contact-form-submit-btn"
                                        disabled={submitting}
                                        className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1D4ED8] px-8 py-4 text-sm font-bold text-white shadow-[0_4px_20px_rgba(37,99,235,0.4)] transition-[background-color,box-shadow,transform] duration-200 hover:bg-[#1E40AF] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {submitting ? "SUBMITTING..." : "REQUEST A FREE QUOTE"}
                                        <ArrowRight className="h-4 w-4" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={sendViaWhatsApp}
                                        data-testid="contact-whatsapp-send-btn"
                                        className="inline-flex items-center justify-center gap-2 rounded-full border border-[#2563EB] bg-white px-8 py-4 text-sm font-bold text-[#1D4ED8] transition-[background-color,color,transform] duration-200 hover:bg-[#F0F5FD] active:scale-[0.98]"
                                    >
                                        <MessageCircle className="h-4 w-4" /> SEND VIA WHATSAPP
                                    </button>
                                </div>
                            </form>
                        </div>
                    </Reveal>

                    <Reveal delay={120}>
                        <div className="flex h-full flex-col rounded-3xl bg-[#0B1528] p-8 text-white shadow-[0_12px_40px_rgb(11,21,40,0.25)]">
                            <Logo dark testid="contact-brand-logo" />
                            <p className="mt-5 text-sm leading-relaxed text-slate-300">
                                Premium Elevator Installation, Maintenance &amp; Breakdown Services.
                            </p>
                            <a
                                href={TEL_URL}
                                data-testid="contact-info-phone"
                                className="mt-7 flex items-center gap-3 rounded-xl border border-white/15 bg-white/5 p-4 transition-colors duration-200 hover:border-[#2563EB]"
                            >
                                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#2563EB]/25 text-[#60A5FA]">
                                    <Phone className="h-5 w-5" />
                                </span>
                                <span>
                                    <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                                        Call Us
                                    </span>
                                    <span className="text-sm font-extrabold">{PHONE_DISPLAY}</span>
                                </span>
                            </a>
                            <a
                                href={WA_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="contact-info-whatsapp"
                                className="mt-3 flex items-center gap-3 rounded-xl border border-white/15 bg-white/5 p-4 transition-colors duration-200 hover:border-[#2563EB]"
                            >
                                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#2563EB]/25 text-[#60A5FA]">
                                    <MessageCircle className="h-5 w-5" />
                                </span>
                                <span>
                                    <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                                        WhatsApp
                                    </span>
                                    <span className="text-sm font-extrabold">{PHONE_DISPLAY}</span>
                                </span>
                            </a>
                            <div className="mt-7 border-t border-white/10 pt-6">
                                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                                    Service Locations
                                </p>
                                <ul className="mt-4 space-y-3">
                                    {LOCATIONS.map((loc) => (
                                        <li key={loc.city} className="flex items-start gap-2.5 text-sm text-slate-300">
                                            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#60A5FA]" />
                                            <span>
                                                <span className="font-bold text-white">{loc.city}</span> — {loc.short}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Emergency */}
            <section className="relative overflow-hidden bg-[#0E1E38] py-24">
                <div
                    className="absolute inset-0 opacity-[0.12]"
                    style={{
                        backgroundImage:
                            "linear-gradient(rgba(96,165,250,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(96,165,250,0.5) 1px, transparent 1px)",
                        backgroundSize: "44px 44px",
                    }}
                />
                <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
                    <Reveal>
                        <p className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.25em] text-[#93C5FD]">
                            <span className="pulse-dot h-2 w-2 rounded-full bg-[#60A5FA]" />
                            Emergency Support
                        </p>
                        <h2 className="mt-5 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                            ELEVATOR BREAKDOWN?
                        </h2>
                        <p className="mt-3 text-lg font-bold uppercase tracking-[0.2em] text-[#60A5FA]">
                            Call Us Now
                        </p>
                        <a
                            href={TEL_URL}
                            data-testid="emergency-phone-number"
                            className="mt-3 block text-4xl font-extrabold tracking-tight text-white transition-colors duration-200 hover:text-[#60A5FA] sm:text-5xl"
                        >
                            {PHONE_DISPLAY}
                        </a>
                        <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
                            <a
                                href={TEL_URL}
                                data-testid="emergency-banner-call-btn"
                                className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-bold text-[#0B1528] shadow-lg transition-[background-color,transform] duration-200 hover:bg-slate-100 active:scale-95"
                            >
                                <Phone className="h-4 w-4" /> CALL NOW
                            </a>
                            <a
                                href={WA_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="emergency-banner-whatsapp-btn"
                                className="inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-8 py-4 text-sm font-bold text-white shadow-[0_4px_20px_rgba(37,99,235,0.45)] transition-[background-color,transform] duration-200 hover:bg-[#1D4ED8] active:scale-95"
                            >
                                <MessageCircle className="h-4 w-4" /> WHATSAPP US
                            </a>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Offices */}
            <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
                <Reveal className="max-w-2xl">
                    <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#2563EB]">
                        Visit Us
                    </p>
                    <h2 className="mt-4 text-3xl font-bold tracking-tight text-[#0B1528] sm:text-4xl">
                        OFFICE LOCATIONS
                    </h2>
                </Reveal>
                <div className="mt-12 grid gap-6 md:grid-cols-3">
                    {LOCATIONS.map((loc, i) => (
                        <Reveal
                            key={loc.city}
                            delay={i * 100}
                            className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_4px_20px_rgb(15,23,42,0.04)] transition-[box-shadow,border-color,transform] duration-300 hover:-translate-y-1 hover:border-blue-300 hover:shadow-[0_16px_40px_rgb(37,99,235,0.12)]"
                        >
                            <div
                                className="relative flex h-40 items-center justify-center bg-[#0B1528]"
                                style={{
                                    backgroundImage:
                                        "linear-gradient(rgba(96,165,250,0.14) 1px, transparent 1px), linear-gradient(90deg, rgba(96,165,250,0.14) 1px, transparent 1px)",
                                    backgroundSize: "26px 26px",
                                }}
                            >
                                <span className="pulse-dot flex h-12 w-12 items-center justify-center rounded-full bg-[#2563EB]/25">
                                    <MapPin className="h-6 w-6 text-[#60A5FA]" />
                                </span>
                                <span className="absolute bottom-3 rounded-full bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#93C5FD] backdrop-blur-sm">
                                    {loc.city}, {loc.state}
                                </span>
                            </div>
                            <div className="p-6">
                                <h3 className="text-lg font-extrabold tracking-wide text-[#0B1528]">
                                    {loc.city} OFFICE
                                </h3>
                                <p className="mt-2 text-sm leading-relaxed text-slate-500">{loc.address}</p>
                                <a
                                    href={TEL_URL}
                                    className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#1D4ED8] transition-colors duration-200 hover:text-[#1E40AF]"
                                >
                                    <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
                                </a>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>
        </>
    );
}
